/**
 * NegoDrawer — Détail latéral d'une négociation : infos, offres, SLA, historique, actions rapides.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, CheckCircle2, XCircle, ArrowLeftRight, AlertTriangle, UserPlus,
  MessageSquarePlus, Download, ExternalLink, Clock, Loader2,
} from 'lucide-react';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  fmtXof, fmtDateTime, fmtRelative, STATUS_META, slaInfo, slaProgress,
  initialOffer, negotiatedOffer, TERMINAL_STATUSES,
} from './negoMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { headers: { Authorization: 'Bearer cookie-auth' } };

const Field = ({ label, value, valueCls }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm font-medium ${valueCls || 'text-slate-900'}`}>{value ?? '—'}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="mb-2 text-sm font-bold text-slate-900">{children}</h4>
);

export const NegoDrawer = ({ negotiationId, seed, open, onClose, onActionDone, assignees = [] }) => {
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null); // counter | reject | assign | note
  const [msg, setMsg] = useState('');
  const [counterPrice, setCounterPrice] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!negotiationId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/negotiations/${negotiationId}`, AUTH);
      setDetail(data.negotiation || null);
      setMessages(data.messages || []);
    } catch (e) { /* keep seed */ } finally { setLoading(false); }
  }, [negotiationId]);

  useEffect(() => {
    if (open && negotiationId) fetchDetail();
    if (!open) { setMode(null); setMsg(''); setCounterPrice(''); setAssigneeId(''); setShowAllHistory(false); }
  }, [open, negotiationId, fetchDetail]);

  const nego = detail || seed || {};
  const status = STATUS_META[nego.status] || { label: nego.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  const isTerminal = TERMINAL_STATUSES.includes(nego.status);
  const sla = slaInfo(nego);
  const prog = slaProgress(nego);
  const init = initialOffer(nego);
  const neg = negotiatedOffer(nego);
  const client = nego.clientName || seed?.clientName;
  const trajet = (nego.departureCity || seed?.departureCity)
    ? `${nego.departureCity || seed?.departureCity} → ${nego.arrivalCity || seed?.arrivalCity || '—'}` : '—';
  const responsable = nego.assignedSalesName || nego.decisionOwnerName || nego.createdByName;

  const run = async (action, extra = {}) => {
    setBusy(true);
    try {
      const body = { action, message: msg || undefined, ...extra };
      await axios.post(`${API}/negotiations/${negotiationId}/actions`, body, AUTH);
      const labels = { accept: 'Négociation acceptée avec succès', reject: 'Négociation refusée', counter: 'Contre-offre envoyée', escalate: 'Dossier escaladé au Lead', assign: 'Responsable assigné' };
      toast.success(labels[action] || 'Action enregistrée');
      setMode(null); setMsg(''); setCounterPrice(''); setAssigneeId('');
      await fetchDetail(); onActionDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'action"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/negotiations/${negotiationId}/messages`, { message: msg }, AUTH);
      toast.success('Note ajoutée');
      setMode(null); setMsg(''); await fetchDetail(); onActionDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setBusy(false); }
  };

  const submitCounter = () => {
    if (!counterPrice || isNaN(Number(counterPrice))) { toast.error('Montant invalide'); return; }
    run('counter', { offerPayload: { proposedPrice: Number(counterPrice), previousPrice: neg ?? init } });
  };
  const submitAssign = () => {
    if (!assigneeId) { toast.error('Sélectionnez un responsable'); return; }
    const a = assignees.find((x) => x.id === assigneeId);
    run('assign', { assigneeId, assigneeName: a?.fullName });
  };

  const downloadRecap = () => {
    const lines = [
      `Détail négociation ${nego.operationNumber || ''}`,
      `Statut : ${status.label}`,
      `Client : ${client || '—'}`,
      `Trajet : ${trajet}`,
      `Responsable : ${responsable || '—'}`,
      `Offre initiale : ${init != null ? fmtXof(init) : '—'}`,
      `Offre négociée : ${neg != null ? fmtXof(neg) : '—'}`,
      `Créée le : ${fmtDateTime(nego.createdAt)}`,
      `Échéance : ${fmtDateTime(nego.slaDeadlineAt)}`,
    ].join('\n');
    const blob = new Blob([`\ufeff${lines}`], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `negociation_${nego.operationNumber || nego.id}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const historyItems = showAllHistory ? messages : messages.slice(-3);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[460px]" data-testid="nego-drawer">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-bold text-slate-900">Détail de la négociation</h3>
          <button onClick={onClose} data-testid="nego-drawer-close" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${status.cls}`}>{status.label}</span>
          <div className="mt-3 font-mono text-lg font-bold text-slate-900">{nego.operationNumber || '—'}</div>
          <div className="text-xs text-slate-500">Créée le {fmtDateTime(nego.createdAt)}</div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <SectionTitle>Informations générales</SectionTitle>
            <Field label="Client" value={client} />
            <Field label="Trajet" value={trajet} />
            <Field label="Commercial" value={responsable} />
            <Field label="Transporteur proposé" value={nego.currentOffer?.proposedTruckType || '—'} />
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <SectionTitle>Offres</SectionTitle>
            <Field label="Offre initiale" value={init != null ? fmtXof(init) : '—'} />
            <Field label="Contre-offre" value={neg != null ? fmtXof(neg) : '—'} />
            <Field label="Min acceptable" value={nego.currentOffer?.customFields?.minAcceptable != null ? fmtXof(nego.currentOffer.customFields.minAcceptable) : '—'} />
            <Field label="Marge estimée" value="—" valueCls="text-slate-400" />
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <SectionTitle>SLA</SectionTitle>
            <Field label="Créée le" value={fmtDateTime(nego.createdAt)} />
            <Field label="Échéance" value={fmtDateTime(nego.slaDeadlineAt)} />
            <div className="flex items-center justify-between py-1.5">
              <span className="text-sm text-slate-500">Temps restant</span>
              <span className={`inline-flex items-center gap-1 text-sm font-semibold ${sla.cls}`}>
                <Clock className="h-3.5 w-3.5" /> {sla.remainingMin == null ? '—' : sla.label.replace(' restantes', '')}
              </span>
            </div>
            {sla.remainingMin != null && (
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${prog.overdue ? 'bg-rose-500' : prog.pct > 70 ? 'bg-orange-500' : 'bg-emerald-500'}`} style={{ width: `${prog.pct}%` }} />
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <SectionTitle>Historique</SectionTitle>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-4 animate-pulse rounded bg-slate-100" />)}</div>
            ) : historyItems.length === 0 ? (
              <p className="text-sm italic text-slate-400">Aucun événement.</p>
            ) : (
              <div className="space-y-3">
                {historyItems.map((m) => (
                  <div key={m.id} className="flex gap-3 text-sm" data-testid={`nego-history-${m.id}`}>
                    <span className="shrink-0 font-mono text-xs text-slate-400">{new Date(m.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="text-slate-700">{m.message}</span>
                  </div>
                ))}
              </div>
            )}
            {messages.length > 3 && (
              <button onClick={() => setShowAllHistory((v) => !v)} data-testid="nego-history-toggle"
                className="mt-3 w-full rounded-md border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                {showAllHistory ? 'Réduire' : "Voir tout l'historique"}
              </button>
            )}
          </div>
        </div>

        {/* Actions rapides */}
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          <SectionTitle>Actions rapides</SectionTitle>
          {mode === 'counter' && (
            <div className="mb-3 space-y-2">
              <Input type="number" placeholder="Nouveau prix (XOF)" value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)} data-testid="nego-counter-price" className="text-sm" />
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => setMode(null)}>Annuler</Button>
                <Button size="sm" className="flex-1 bg-blue-600 text-white hover:bg-blue-700" onClick={submitCounter} disabled={busy} data-testid="nego-counter-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Envoyer'}</Button>
              </div>
            </div>
          )}
          {mode === 'reject' && (
            <div className="mb-3 space-y-2">
              <Textarea rows={2} placeholder="Motif du refus…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="nego-reject-msg" className="resize-none text-sm" />
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => setMode(null)}>Annuler</Button>
                <Button size="sm" className="flex-1 bg-rose-600 text-white hover:bg-rose-700" onClick={() => run('reject')} disabled={busy} data-testid="nego-reject-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer le refus'}</Button>
              </div>
            </div>
          )}
          {mode === 'assign' && (
            <div className="mb-3 space-y-2">
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger className="text-sm" data-testid="nego-assign-select"><SelectValue placeholder="Choisir un responsable" /></SelectTrigger>
                <SelectContent>{assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.fullName}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => setMode(null)}>Annuler</Button>
                <Button size="sm" className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700" onClick={submitAssign} disabled={busy} data-testid="nego-assign-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assigner'}</Button>
              </div>
            </div>
          )}
          {mode === 'note' && (
            <div className="mb-3 space-y-2">
              <Textarea rows={2} placeholder="Note interne…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="nego-note-msg" className="resize-none text-sm" />
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => setMode(null)}>Annuler</Button>
                <Button size="sm" className="flex-1 bg-slate-900 text-white hover:bg-slate-800" onClick={addNote} disabled={busy} data-testid="nego-note-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ajouter'}</Button>
              </div>
            </div>
          )}

          {!mode && (
            <>
              {!isTerminal && (
                <div className="grid grid-cols-3 gap-2">
                  <Button size="sm" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100" onClick={() => run('accept')} disabled={busy} data-testid="nego-action-accept"><CheckCircle2 className="mr-1 h-4 w-4" /> Accepter</Button>
                  <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => setMode('reject')} data-testid="nego-action-reject"><XCircle className="mr-1 h-4 w-4" /> Refuser</Button>
                  {nego.status !== 'countered' && <Button size="sm" variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50" onClick={() => setMode('counter')} data-testid="nego-action-counter"><ArrowLeftRight className="mr-1 h-4 w-4" /> Contre-offre</Button>}
                </div>
              )}
              <div className="mt-2 grid grid-cols-3 gap-2">
                {!isTerminal && <Button size="sm" variant="ghost" className="text-purple-600 hover:bg-purple-50" onClick={() => run('escalate')} disabled={busy} data-testid="nego-action-escalate"><AlertTriangle className="mr-1 h-4 w-4" /> Escalader</Button>}
                {!isTerminal && <Button size="sm" variant="ghost" className="text-slate-700 hover:bg-slate-100" onClick={() => setMode('assign')} data-testid="nego-action-assign"><UserPlus className="mr-1 h-4 w-4" /> Assigner</Button>}
                <Button size="sm" variant="ghost" className="text-slate-700 hover:bg-slate-100" onClick={() => setMode('note')} data-testid="nego-action-note"><MessageSquarePlus className="mr-1 h-4 w-4" /> Note</Button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" className="text-slate-700" onClick={downloadRecap} data-testid="nego-action-download"><Download className="mr-1 h-4 w-4" /> Récapitulatif</Button>
                {nego.operationId && <Button size="sm" variant="outline" className="text-slate-700" onClick={() => window.open(`/admin/operations/${nego.operationId}`, '_blank')} data-testid="nego-action-view-op"><ExternalLink className="mr-1 h-4 w-4" /> Opération</Button>}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NegoDrawer;
